import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Database, GitBranch, Link, ChevronRight } from 'lucide-react';
import * as api from '../api';

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<'general' | 'repos' | 'advanced'>('general');

  const reposQuery = useQuery({
    queryKey: ['repos'],
    queryFn: api.getConnectedRepos,
  });

  const mappingsQuery = useQuery({
    queryKey: ['mappings'],
    queryFn: api.getMappings,
  });

  const disconnectRepoMutation = useMutation({
    mutationFn: api.disconnectRepo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repos'] });
    },
  });

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500 mb-6">
          Manage your workspace configuration
        </p>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {/* Settings Navigation */}
          <div className="border-b">
            <nav className="flex">
              <button
                onClick={() => setActiveSection('general')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeSection === 'general'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                General
              </button>
              <button
                onClick={() => setActiveSection('repos')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeSection === 'repos'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Repositories
              </button>
              <button
                onClick={() => setActiveSection('advanced')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeSection === 'advanced'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Advanced
              </button>
            </nav>
          </div>

          {/* General Settings */}
          {activeSection === 'general' && (
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">CDN Configuration</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Link className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">CDN Base URL</p>
                        <p className="text-sm text-gray-500">https://cdn.getsmartup.ai/workspaces</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Database className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">S3 Bucket</p>
                        <p className="text-sm text-gray-500">smartup-workspaces-cdn</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">GitHub Organization</h3>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <GitBranch className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">SmartUp-Chile</p>
                      <p className="text-sm text-gray-500">Workspace repositories are created here automatically</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <p className="text-xs text-gray-400">
                  Workspaces are automatically mapped to GitHub repositories with the naming pattern: <code className="bg-gray-100 px-1 rounded">ws-{'{workspace-name}'}</code>
                </p>
              </div>
            </div>
          )}

          {/* Repository Settings */}
          {activeSection === 'repos' && (
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Connected Repositories</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Repositories are automatically created when you create a new workspace
                </p>

                {reposQuery.isLoading ? (
                  <div className="animate-pulse space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded-lg" />
                    ))}
                  </div>
                ) : reposQuery.data?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <GitBranch className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No repositories connected yet</p>
                    <p className="text-sm">Create a workspace to automatically create a repository</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {reposQuery.data?.map((repo: any) => (
                      <div
                        key={repo.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <GitBranch className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{repo.fullName}</p>
                            <p className="text-xs text-gray-500">Connected {new Date(repo.connectedAt).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Workspace Mappings</h3>
                <p className="text-sm text-gray-500 mb-4">
                  How workspaces map to GitHub repositories
                </p>

                {mappingsQuery.isLoading ? (
                  <div className="animate-pulse space-y-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded-lg" />
                    ))}
                  </div>
                ) : mappingsQuery.data?.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No mappings configured</p>
                ) : (
                  <div className="overflow-hidden rounded-lg border">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workspace</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Repository</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {mappingsQuery.data?.map((mapping: any) => (
                          <tr key={mapping.id}>
                            <td className="px-4 py-3 text-sm text-gray-900">{mapping.workspaceName}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{mapping.githubRepo}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{mapping.branch}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Advanced Settings */}
          {activeSection === 'advanced' && (
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">API Configuration</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">API Endpoint</p>
                      <p className="text-sm text-gray-500">http://localhost:3001/api</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">CloudFront Distribution</p>
                      <p className="text-sm text-gray-500">E3FIOZTRFF08EE</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-4">CLI Integration</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Use the Claude Code plugin to interact with workspaces programmatically
                </p>
                <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm text-gray-300">
                  <p className="text-gray-500"># Source the plugin</p>
                  <p>source ~/.claude/plugins/agent-working-directories/lib/workspace.sh</p>
                  <p className="mt-2 text-gray-500"># List workspaces</p>
                  <p>list_workspaces</p>
                  <p className="mt-2 text-gray-500"># Read a file</p>
                  <p>read_file "workspace-name" "/path/to/file.md"</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
